import { AddressLocation, GetMaterialCustomList, MaterialGroup, UnitOfMaterial } from '@/api'
import store from '@/store'
import { INPUT_TYPES, MATERIAL_FIXED_FIELDS, OPERATORS, PLUGIN_FIXED_FIELDS, SOURCE_ITEMS } from './configConstants'
import { clone, createAction, createConditionGroup } from './configModel'
import config from '@/config'

export function optionValue (option) {
  if (option && typeof option === 'object') {
    return option.value ?? option.id ?? option.name
  }
  return option
}

export function optionLabel (option) {
  if (Array.isArray(option)) {
    return option.map(item => optionLabel(item)).filter(Boolean).join(', ')
  }
  if (option && typeof option === 'object') {
    return option.field_name ?? option.label ?? option.title ?? option.name ?? option.value ?? option.id
  }
  if (option === true) return 'Yes'
  if (option === false) return 'No'
  return option
}

export function optionDisplayLabel (option) {
  return optionLabel(option) || optionValue(option) || ''
}

export function normalizeInputType (inputType) {
  if (typeof inputType === 'string' && inputType.trim() !== '') {
    const numericType = Number(inputType)
    if (Number.isInteger(numericType)) {
      return numericType
    }
  }

  return inputType
}

export function getFieldType (field = {}) {
  return normalizeInputType(field?.type ?? field?.data_type ?? field?.field_type)
}

export function getComparableFieldType (field = {}) {
  const normalizedType = getFieldType(field)

  switch (normalizedType) {
  case INPUT_TYPES.TEXT:
  case INPUT_TYPES.LONG_TEXT:
    return 'TEXT'
  case INPUT_TYPES.NUMBER:
    return 'NUMBER'
  case INPUT_TYPES.CUSTOM_LIST:
    return 'CUSTOM_LIST'
  case INPUT_TYPES.DATE:
    return 'DATE'
  case INPUT_TYPES.BOOLEAN:
    return 'BOOLEAN'
  default:
    return normalizedType || null
  }
}

export function getComparableFieldTypeLabel (comparableType) {
  switch (comparableType) {
  case 'TEXT':
    return 'String'
  case 'NUMBER':
    return 'Number'
  case 'CUSTOM_LIST':
    return 'Dropdown'
  case 'DATE':
    return 'Date'
  case 'BOOLEAN':
    return 'Boolean'
  default:
    return 'matching'
  }
}

export function isFieldTypeCompatible (targetField = {}, sourceField = {}) {
  return getComparableFieldType(targetField) === getComparableFieldType(sourceField)
}

export const INVALID_BORDER_COLOR = '#f04438'

export function findOptionByValue (options = [], selected) {
  const selectedValue = optionValue(selected)
  return options.find(option => optionValue(option) === selectedValue)
}

export function resolveActionOption (options = [], selected) {
  if (!hasRequiredValue(selected)) {
    return null
  }

  const selectedValue = optionValue(selected)
  const selectedLabel = optionDisplayLabel(selected)

  return options.find((option) => {
    return optionValue(option) === selectedValue ||
      optionDisplayLabel(option) === selectedLabel
  }) || null
}

export function withValidationStyle (style = {}, isInvalid = false) {
  return isInvalid
    ? {
      ...style,
      borderColor: INVALID_BORDER_COLOR
    }
    : style
}

export function hasRequiredValue (value) {
  if (typeof value === 'boolean') {
    return true
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value)
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (value && typeof value === 'object') {
    return hasRequiredValue(optionValue(value))
  }

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  return value !== null && value !== undefined && value !== ''
}

export function isNegativeValue (value) {
  if (value === null || value === undefined || value === '') return false
  const num = Number(value)
  return !Number.isNaN(num) && num < 0
}

export function shouldRestrictNegativeConditionValue (condition = {}) {
  return optionValue(condition?.fieldType) === 'MATERIAL_FIXED_FIELDS'
}

export function getConditionValidationState (condition = {}) {
  const isNegative = shouldRestrictNegativeConditionValue(condition) && isNegativeValue(condition?.value)
  return {
    fieldType: !hasRequiredValue(condition?.fieldType),
    field: !hasRequiredValue(condition?.field),
    operator: !hasRequiredValue(condition?.operator),
    value: !hasRequiredValue(condition?.value) || isNegative,
    negativeValue: isNegative
  }
}

export function isConditionComplete (condition = {}) {
  return !Object.values(getConditionValidationState(condition)).some(Boolean)
}

export function shouldRestrictNegativeActionValue (action = {}, selectedConfig = null) {
  return optionValue(selectedConfig) === 'MATERIAL_FIXED_FIELDS' || optionValue(action?.source) === 'MATERIAL_FIXED_FIELDS'
}

export function getActionValidationState (action = {}, selectedConfig = null) {
  const isSequenceTemplate = isSequenceTemplateConfig(selectedConfig)
  const isMapAction = optionValue(action?.type) === 'MAP'
  const isNegative = shouldRestrictNegativeActionValue(action, selectedConfig) && isNegativeValue(action?.value)

  return {
    field: !isSequenceTemplate && !hasRequiredValue(action?.field),
    source: isMapAction && !hasRequiredValue(action?.source),
    value: !hasRequiredValue(action?.value) || isNegative,
    negativeValue: isNegative
  }
}

export function isActionComplete (action = {}, selectedConfig = null) {
  return !Object.values(getActionValidationState(action, selectedConfig)).some(Boolean)
}

// material condition helper starts
// Configurations that may also be referenced as a condition source for themselves.
// Self references are still guarded against loops by detectCircularDependencies.
const SELF_REFERENCEABLE_CONFIGS = ['STANDARD_MATERIAL_FORM']

export function fetchMaterialSourceItems ({ selectedConfig }) {
  const configValue = optionValue(selectedConfig)
  if (SELF_REFERENCEABLE_CONFIGS.includes(configValue)) {
    return SOURCE_ITEMS
  }
  return SOURCE_ITEMS.filter(item => item.id !== configValue)
}

export function getMaterialCondtions ({ selectedSource }) {
  if (optionValue(selectedSource) === 'MATERIAL_FIXED_FIELDS') {
    return MATERIAL_FIXED_FIELDS
  }
  if (optionValue(selectedSource) === 'PLUGIN_FIXED_FIELDS') {
    return PLUGIN_FIXED_FIELDS
  }
  if (optionValue(selectedSource) === 'STANDARD_MATERIAL_FORM') {
    return store.getters['tenantConfig/getSmfList'] || []
  }
  return []
}

export function findConditionField ({ selectedSource, fieldId }) {
  if (!fieldId) {
    return null
  }

  return getMaterialCondtions({ selectedSource }).find(item => optionValue(item) === fieldId) || null
}

export function fetchSourceItems ({ selectedTab, selectedConfig, sourceFieldTypes }) {
  if (selectedTab === 'MATERIAL') {
    return fetchMaterialSourceItems({ selectedConfig, sourceFieldTypes })
  }
  return sourceFieldTypes
}
export const formatTemplateFieldIds = (activeVersion) => {
  const configIdSet = new Set()
  const smfList = []

  for (const field of activeVersion?.template_fields || []) {
    if (field.autogenerated) continue
    if (field.field_type_id === config.FORM_TYPE.CONFIGRATION_LIST) {
      configIdSet.add(field.custom_list_id)
    }
    smfList.push({
      id: field.field_id,
      name: field.caption || field.field_name,
      fieldName: field.field_name,
      type: normalizeInputType(field.field_type_id),
      customListId: toIntegerId(field.custom_list_id) ?? field.custom_list_id
    })
  }

  return { configIdSet, smfList }
}

export function getActionTypes (selectedConfig) {
  if (optionValue(selectedConfig) === 'SEQUENCE_TEMPLATE') {
    return []
  }
  if (selectedConfig.id === 'STANDARD_MATERIAL_FORM') {
    return store.getters['tenantConfig/getSmfList'] || []
  }
  if (selectedConfig.id === 'MATERIAL_FIXED_FIELDS') {
    return MATERIAL_FIXED_FIELDS || []
  }
  return []
}

export function getActionOperations (selectedConfig) {
  const operations = [
    { id: 'SET', name: 'SET', value: 'SET' },
    { id: 'MAP', name: 'MAP', value: 'MAP' }
  ]

  if (optionValue(selectedConfig) === 'SEQUENCE_TEMPLATE') {
    return operations.filter(operation => operation.id === 'SET')
  }

  return operations
}

export function isSequenceTemplateConfig (selectedConfig) {
  return optionValue(selectedConfig) === 'SEQUENCE_TEMPLATE'
}

export function buildRuleStateForConfiguration (rule, selectedConfig, conditionCount = 2) {
  const nextRule = clone(rule)
  nextRule.configuration = selectedConfig
  nextRule.conditionGroups = [createConditionGroup(conditionCount)]
  nextRule.conditionGroups = nextRule.conditionGroups.map((group) => {
    const seededConditions = []
    return {
      ...group,
      conditions: group.conditions.map((condition) => {
        const nextCondition = {
          ...condition,
          ...getDefaultConditionSelection({
            selectedTab: nextRule.feature,
            selectedConfig,
            groupConditions: seededConditions
          })
        }
        seededConditions.push(nextCondition)
        return nextCondition
      })
    }
  })
  nextRule.actions = [createAction()]
  return nextRule
}

export function buildConditionFieldKey (condition = {}) {
  const fieldType = optionValue(condition?.fieldType)
  const field = optionValue(condition?.field)

  if (!fieldType || !field) {
    return ''
  }

  return `${fieldType}::${field}`
}

export function resetRuleForConfiguration (rule, selectedConfig) {
  return buildRuleStateForConfiguration(rule, selectedConfig, 2)
}

export function isIncludesOperator (operator) {
  return optionValue(operator) === 'INCLUDES'
}

export function normalizeStringListValue (value) {
  if (Array.isArray(value)) {
    return value
      .map(item => item === undefined || item === null ? '' : String(item).trim())
      .filter(Boolean)
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    return trimmedValue ? [trimmedValue] : []
  }

  return []
}

export function getConditionStoredValue (condition = {}) {
  if (isIncludesOperator(condition?.operator)) {
    if (Array.isArray(condition.int_list) && condition.int_list.length) {
      return condition.int_list.map(id => ({ id }))
    }

    if (Array.isArray(condition.string_list) && condition.string_list.length) {
      return normalizeStringListValue(condition.string_list)
    }

    if (hasRequiredValue(condition.string_value)) {
      return normalizeStringListValue(condition.string_value)
    }
  }

  return condition.bool_value ?? condition.date_value ??
    condition.string_value ??
    (condition.int_value !== null && condition.int_value !== undefined ? { id: condition.int_value } : null) ??
    condition.string_list ?? condition.int_list
}

export function toIntegerId (value) {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const numericValue = Number(value)
    if (Number.isInteger(numericValue) && `${numericValue}` === value.trim()) {
      return numericValue
    }
  }

  return null
}

export function usesNativeIntegerIds (field) {
  if (!field || normalizeInputType(field.type) !== INPUT_TYPES.CUSTOM_LIST) {
    return false
  }

  return Boolean(field.customListId) || ['material_group', 'unit_of_material', 'storage_loc', 'material_type'].includes(field.id)
}

export function getCustomListLookupKey (field) {
  if (!field) {
    return null
  }

  return field.customListId || field.id || null
}

export function getAvailableConditionFields ({
  selectedTab,
  selectedSource,
  groupConditions = [],
  conditionId = null,
  allowDuplicates = false
}) {
  if (selectedTab !== 'MATERIAL') {
    return []
  }

  if (allowDuplicates) {
    return getMaterialCondtions({ selectedSource })
  }

  const selectedFieldType = optionValue(selectedSource)
  const usedFieldIds = groupConditions
    .filter(item => item.id !== conditionId && optionValue(item.fieldType) === selectedFieldType)
    .map(item => optionValue(item.field))

  return getMaterialCondtions({ selectedSource }).filter((item) => {
    return !usedFieldIds.includes(optionValue(item))
  })
}

export function getDefaultConditionSelection ({
  selectedTab,
  selectedConfig,
  sourceFieldTypes = SOURCE_ITEMS,
  groupConditions = [],
  conditionId = null,
  allowDuplicates = false
}) {
  const availableSources = fetchSourceItems({
    selectedTab,
    selectedConfig,
    sourceFieldTypes
  })
  const fieldType = availableSources[0] ? clone(availableSources[0]) : {}
  const nextField = getAvailableConditionFields({
    selectedTab,
    selectedSource: fieldType,
    groupConditions,
    conditionId,
    allowDuplicates
  })[0]

  return {
    fieldType,
    field: nextField ? clone(nextField) : {}
  }
}

export function getDefaultOperatorForField (field) {
  const operators = findOperator(field?.type)
  return operators[0] ? clone(operators[0]) : clone(OPERATORS[0])
}

export function getConfigurationChangeConflicts ({
  selectedTab,
  selectedConfig,
  nextConfig,
  sourceFieldTypes = SOURCE_ITEMS,
  conditionGroups = []
}) {
  const nextSources = fetchSourceItems({
    selectedTab,
    selectedConfig: nextConfig,
    sourceFieldTypes
  })
  const nextSourceIds = nextSources.map(item => optionValue(item))

  return conditionGroups.flatMap((group) => {
    return (group?.conditions || []).filter((condition) => {
      return !nextSourceIds.includes(optionValue(condition.fieldType))
    }).map(condition => ({
      groupId: group.id,
      conditionId: condition.id,
      fieldType: optionValue(condition.fieldType),
      field: optionValue(condition.field)
    }))
  })
}

export function normalizeActionsForConfiguration (selectedConfig, actions = [], previousConfig = null) {
  const nextIsSequenceTemplate = isSequenceTemplateConfig(selectedConfig)
  const previousIsSequenceTemplate = isSequenceTemplateConfig(previousConfig)

  if (nextIsSequenceTemplate || previousIsSequenceTemplate) {
    return [createAction()]
  }

  return Array.isArray(actions) && actions.length ? clone(actions) : [createAction()]
}

export function getAndConnectedGroupIndexes (conditionGroups = [], groupIndex = -1) {
  if (groupIndex < 0 || groupIndex >= conditionGroups.length) {
    return []
  }

  let start = groupIndex
  let end = groupIndex

  while (start > 0 && conditionGroups[start - 1]?.joinWithNext === 'AND') {
    start -= 1
  }

  while (end < conditionGroups.length - 1 && conditionGroups[end]?.joinWithNext === 'AND') {
    end += 1
  }

  return conditionGroups.slice(start, end + 1).map((_, index) => start + index)
}

export function getCrossGroupRestrictedFieldIds ({
  conditionGroups = [],
  groupIndex = -1,
  selectedSource,
  currentConditionId = null
}) {
  const connectedIndexes = getAndConnectedGroupIndexes(conditionGroups, groupIndex)
  const selectedFieldType = optionValue(selectedSource)

  return new Set(
    connectedIndexes
      .filter(index => index !== groupIndex)
      .flatMap(index => conditionGroups[index]?.conditions || [])
      .filter(condition => optionValue(condition?.fieldType) === selectedFieldType && condition.id !== currentConditionId)
      .map(condition => optionValue(condition?.field))
      .filter(Boolean)
  )
}

export function getDuplicateFieldsAcrossGroups ({
  conditionGroups = [],
  leftIndexes = [],
  rightIndexes = []
}) {
  const leftKeys = new Set(
    leftIndexes.flatMap(index => {
      return (conditionGroups[index]?.conditions || [])
        .map(condition => buildConditionFieldKey(condition))
        .filter(Boolean)
    })
  )

  const duplicates = []
  const seenKeys = new Set()

  rightIndexes.forEach((index) => {
    ;(conditionGroups[index]?.conditions || []).forEach((condition) => {
      const key = buildConditionFieldKey(condition)
      if (!key || !leftKeys.has(key) || seenKeys.has(key)) {
        return
      }
      seenKeys.add(key)
      duplicates.push({
        key,
        fieldType: optionValue(condition?.fieldType),
        field: optionValue(condition?.field),
        label: optionLabel(condition?.field) || optionValue(condition?.field)
      })
    })
  })

  return duplicates
}

export function dedupeConditionGroupsForAndMerge ({
  conditionGroups = [],
  mergedIndexes = []
}) {
  const nextGroups = clone(conditionGroups)
  const seenKeys = new Set()

  ;[...mergedIndexes].sort((a, b) => b - a).forEach((groupIndex) => {
    const nextConditions = []

    for (let conditionIndex = (nextGroups[groupIndex]?.conditions || []).length - 1; conditionIndex >= 0; conditionIndex--) {
      const condition = nextGroups[groupIndex].conditions[conditionIndex]
      const key = buildConditionFieldKey(condition)

      if (!key || !seenKeys.has(key)) {
        if (key) {
          seenKeys.add(key)
        }
        nextConditions.unshift(condition)
      }
    }

    nextGroups[groupIndex].conditions = nextConditions
  })

  return nextGroups
}

export function pruneEmptyConditionGroups (conditionGroups = []) {
  const nextGroups = []

  conditionGroups.forEach((group) => {
    const conditions = Array.isArray(group?.conditions) ? group.conditions : []
    if (!conditions.length) {
      return
    }

    nextGroups.push({
      ...group,
      conditions
    })
  })

  return nextGroups.map((group, index) => ({
    ...group,
    joinWithNext: index < nextGroups.length - 1 ? (group.joinWithNext || 'AND') : 'AND'
  }))
}

export function getDuplicateFieldsInGroup (group = {}) {
  const duplicateMap = new Map()
  const seenKeys = new Set()

  ;(group?.conditions || []).forEach((condition) => {
    const key = buildConditionFieldKey(condition)
    if (!key) {
      return
    }

    if (seenKeys.has(key)) {
      if (!duplicateMap.has(key)) {
        duplicateMap.set(key, {
          key,
          fieldType: optionValue(condition?.fieldType),
          field: optionValue(condition?.field),
          label: optionLabel(condition?.field) || optionValue(condition?.field)
        })
      }
      return
    }

    seenKeys.add(key)
  })

  return Array.from(duplicateMap.values())
}

export function dedupeConditionsForAndGroup (group = {}) {
  const nextGroup = clone(group)
  const seenKeys = new Set()
  const nextConditions = []

  for (let index = (nextGroup.conditions || []).length - 1; index >= 0; index--) {
    const condition = nextGroup.conditions[index]
    const key = buildConditionFieldKey(condition)

    if (!key || !seenKeys.has(key)) {
      if (key) {
        seenKeys.add(key)
      }
      nextConditions.unshift(condition)
    }
  }

  nextGroup.conditions = nextConditions
  return nextGroup
}

export function getActionValueItems ({ selectedConfig, lists }) {
  if (isSequenceTemplateConfig(selectedConfig)) {
    return lists.sequenceTemplateValues || []
  }

  if (selectedConfig?.id === 'PLUGIN_FIXED_FIELDS') {
    return PLUGIN_FIXED_FIELDS.map((field) => ({
      ...field,
      id: field.name,
      value: field.name,
      name: field.name
    }))
  }

  if (selectedConfig?.id === 'STANDARD_MATERIAL_FORM') {
    return store.getters['tenantConfig/getSmfList'] || []
  }

  if (selectedConfig?.id === 'MATERIAL_FIXED_FIELDS') {
    return MATERIAL_FIXED_FIELDS || []
  }

  return []
}

export function getActionTargetItems ({
  selectedTab,
  selectedConfig,
  actions = [],
  actionId = null,
  actionType = null
}) {
  if (selectedTab !== 'MATERIAL') {
    return []
  }

  const selectedType = optionValue(actionType)
  const usedFieldIds = actions
    .filter(item => item.id !== actionId && optionValue(item.type) === selectedType)
    .map(item => optionValue(item.field))

  return getActionTypes(selectedConfig).filter((item) => {
    return !usedFieldIds.includes(optionValue(item))
  })
}

export function getMapSourceKey (action = {}) {
  if (optionValue(action.source) === 'PLUGIN_FIXED_FIELDS') {
    return optionDisplayLabel(action.value)
  }

  return optionValue(action.value)
}

export function hasConditionRows (rule = {}) {
  return (rule?.conditionGroups || []).some((group) => Array.isArray(group?.conditions) && group.conditions.length > 0)
}

export function validateConfigurationRules (rules = []) {
  const ruleValidationMap = {}
  let hasInvalidFields = false

  rules.forEach((rule) => {
    const hasMapActions = (rule?.actions || []).some(action => optionValue(action?.type) === 'MAP')
    const hasSetActions = (rule?.actions || []).some(action => optionValue(action?.type) === 'SET')
    const shouldValidateConditions = hasConditionRows(rule)
    const conditionsRequired = hasSetActions && !hasMapActions && !shouldValidateConditions
    const ruleValidation = {
      configuration: !hasRequiredValue(rule?.configuration),
      actionsRequired: !Array.isArray(rule?.actions) || rule.actions.length === 0,
      conditionsRequired,
      conditions: {},
      actions: {}
    }

    if (ruleValidation.configuration || ruleValidation.actionsRequired || ruleValidation.conditionsRequired) {
      hasInvalidFields = true
    }

    if (shouldValidateConditions) {
      ;(rule?.conditionGroups || []).forEach((group) => {
        ;(group?.conditions || []).forEach((condition) => {
          const conditionValidation = getConditionValidationState(condition)

          if (Object.values(conditionValidation).some(Boolean)) {
            hasInvalidFields = true
          }

          ruleValidation.conditions[condition.id] = conditionValidation
        })
      })
    }

    ;(rule?.actions || []).forEach((action) => {
      const actionValidation = getActionValidationState(action, rule?.configuration)

      if (Object.values(actionValidation).some(Boolean)) {
        hasInvalidFields = true
      }

      ruleValidation.actions[action.id] = actionValidation
    })

    if (isSequenceTemplateConfig(rule?.configuration) && (!Array.isArray(rule?.actions) || rule.actions.length !== 1)) {
      hasInvalidFields = true
    }

    ruleValidationMap[rule.id] = ruleValidation
  })

  return {
    isValid: !hasInvalidFields,
    ruleValidationMap
  }
}
// material condition helper ends
// bom condition helper starts
// bom condition helper ends
// factory condition helper starts
// factory condition helper ends

// common functions
export function findOperator (inputType) {
  const operators = inputType
    ? OPERATORS.filter(operator => operator.applicableTo.includes(inputType))
    : OPERATORS
  return operators
    .filter(operator => operator?.id && operator?.name)
    .map(operator => ({ ...operator }))
}
export async function loadListItems ({ selectedCondition } = {}) {
  switch (selectedCondition?.id) {
  case 'unit_of_material': {
    const data = await UnitOfMaterial()
    return data.custom_list_values.map(item => ({ name: item.name, id: item.name }))
  }
  case 'material_group': {
    const data = await MaterialGroup()
    return data.custom_list_values.map(item => ({ name: item.name, id: item.name }))
  }
  case 'storage_loc': {
    const data = await AddressLocation()
    return data.address_locations.map(item => ({ name: item.name, id: item.name }))
  }
  case 'material_type': {
    const data = await GetMaterialCustomList()
    return data.custom_list_values.map(item => ({ name: item.name, id: item.name }))
  }
  default:
    return []
  }
}
export const formatToDropDownInput = (ITEMS, id) => {
  if (id && typeof id === 'object') {
    return id
  }
  return ITEMS.find(e => e.id === id || e.id?.toLowerCase() === `${id}`.toLowerCase())
}

// circular dependency detection

export function getRuleFieldIO (rule = {}) {
  const reads = new Set()
  const writes = new Set()

  for (const group of (rule.conditionGroups || [])) {
    for (const condition of (group.conditions || [])) {
      const sourceType = optionValue(condition.fieldType)
      const fieldId = optionValue(condition.field)
      if (sourceType && fieldId) {
        reads.add(`${sourceType}::${fieldId}`)
      }
    }
  }

  const configType = optionValue(rule.configuration)
  for (const action of (rule.actions || [])) {
    const fieldId = optionValue(action.field)
    if (configType && fieldId) {
      writes.add(`${configType}::${fieldId}`)
    }
  }

  return { reads, writes }
}

export function buildRuleDependencyGraph (rules = []) {
  const ruleDeps = rules.map(rule => ({
    id: rule.id,
    ...getRuleFieldIO(rule)
  }))

  const adjacency = new Map()
  const inDegree = new Map()
  const edges = []

  for (const dep of ruleDeps) {
    adjacency.set(dep.id, new Set())
    inDegree.set(dep.id, 0)
  }

  for (const writer of ruleDeps) {
    for (const reader of ruleDeps) {
      if (String(writer.id) === String(reader.id)) continue

      const sharedFields = []
      for (const wKey of writer.writes) {
        if (reader.reads.has(wKey)) {
          sharedFields.push(wKey)
        }
      }

      if (sharedFields.length > 0 && !adjacency.get(writer.id).has(reader.id)) {
        adjacency.get(writer.id).add(reader.id)
        inDegree.set(reader.id, (inDegree.get(reader.id) || 0) + 1)
        sharedFields.forEach(field => edges.push({ from: writer.id, to: reader.id, field }))
      }
    }
  }

  return { adjacency, inDegree, edges, ruleDeps }
}

export function getSelfReferencingEdges (ruleDeps = []) {
  const selfEdges = []
  for (const dep of ruleDeps) {
    for (const writeKey of dep.writes) {
      if (dep.reads.has(writeKey)) {
        selfEdges.push({ from: dep.id, to: dep.id, field: writeKey })
      }
    }
  }
  return selfEdges
}

export function detectCircularDependencies (rules = []) {
  const empty = { hasCycle: false, cycleRuleIds: [], conflictingFields: [], cycleEdges: [] }
  if (!rules.length) return empty

  const { adjacency, inDegree, edges, ruleDeps } = buildRuleDependencyGraph(rules)

  // A rule that reads a field it also writes is a cycle on its own, and the
  // topological pass below skips self edges, so collect them separately.
  const selfEdges = getSelfReferencingEdges(ruleDeps)

  const buildResult = (cycleEdges) => {
    if (!cycleEdges.length) return empty
    return {
      hasCycle: true,
      cycleRuleIds: [...new Set(cycleEdges.flatMap(edge => [edge.from, edge.to]))],
      conflictingFields: [...new Set(cycleEdges.map(edge => edge.field))],
      cycleEdges
    }
  }

  if (rules.length < 2) return buildResult(selfEdges)

  const queue = []
  for (const [id, degree] of inDegree) {
    if (degree === 0) queue.push(id)
  }

  const sorted = []
  while (queue.length > 0) {
    const node = queue.shift()
    sorted.push(node)
    for (const neighbor of adjacency.get(node)) {
      const next = inDegree.get(neighbor) - 1
      inDegree.set(neighbor, next)
      if (next === 0) queue.push(neighbor)
    }
  }

  if (sorted.length === adjacency.size) return buildResult(selfEdges)

  const sortedSet = new Set(sorted.map(String))
  const cycleRuleIds = [...adjacency.keys()].filter(id => !sortedSet.has(String(id)))

  const cycleEdges = edges.filter(
    e => cycleRuleIds.some(id => String(id) === String(e.from)) &&
         cycleRuleIds.some(id => String(id) === String(e.to))
  )

  return buildResult([...selfEdges, ...cycleEdges])
}

export function formatDependencyFieldKey (fieldKey = '') {
  const [sourceType, fieldId] = fieldKey.split('::')
  if (!sourceType || !fieldId) return fieldKey

  const sourceLabel = (sourceType || '').replace(/_/g, ' ')

  const smfList = store.getters['tenantConfig/getSmfList'] || []
  const allFields = [...MATERIAL_FIXED_FIELDS, ...PLUGIN_FIXED_FIELDS, ...smfList]
  const field = allFields.find(f => f.id === fieldId)
  const fieldLabel = field ? (field.name || field.fieldName) : fieldId

  return `${fieldLabel} (${sourceLabel})`
}
// common functions
